import { PrivacyBlur } from "#components/privacy-blur"
import { useTheme } from "#components/theme-provider"
import { Card, CardContent, CardTitle } from "#components/ui/card"
import { StatLabel, StatValue } from "#components/ui/typography"
import { useCurrencyFormatters } from "#hooks/use-currency-formatters"
import { useI18n } from "#hooks/use-i18n"
import { useThemeColors } from "#hooks/use-theme-colors"
import { cardEntrance, getCardHoverEffect, statValue } from "#lib/animations"
import type { DashboardAnalyticsResponse } from "@home-finance-manager/shared/types/dashboard"
import { motion } from "framer-motion"
import React, { useMemo } from "react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

interface CategoryComparisonCardProps {
  analytics: DashboardAnalyticsResponse | undefined
}

export const CategoryComparisonCard = React.memo<CategoryComparisonCardProps>(({ analytics }) => {
  const { t } = useI18n()
  const { formatCurrency } = useCurrencyFormatters()
  const themeColors = useThemeColors()
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"

  // Get expense sources for all months in the date range
  const expenseSourcesByMonth = analytics?.expenseSourcesByMonth || []

  // Get top 5-6 categories across all months
  const topCategories = useMemo(() => {
    const categoryMap = new Map<
      number,
      { name: string; color: string; amounts: number[]; total: number }
    >()

    // Aggregate category data across all months
    expenseSourcesByMonth.forEach((monthData) => {
      monthData.expenseSources.forEach((source) => {
        const existing = categoryMap.get(source.categoryId)
        if (existing) {
          existing.amounts.push(source.amount)
          existing.total += source.amount
        } else {
          categoryMap.set(source.categoryId, {
            name: source.categoryName,
            color: source.color,
            amounts: [source.amount],
            total: source.amount,
          })
        }
      })
    })

    // Sort by total and get top 6
    return Array.from(categoryMap.entries())
      .map(([categoryId, data]) => ({
        categoryId,
        ...data,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6)
  }, [expenseSourcesByMonth])

  // Build chart data - one entry per month with values for each category
  const chartData = useMemo(() => {
    return expenseSourcesByMonth.map((monthData) => {
      const dataPoint: Record<string, string | number> = {
        month: monthData.monthLabel.split(" ")[0], // Short month name
        monthLabel: monthData.monthLabel, // Full month label for tooltip
      }

      // Add amount for each top category
      topCategories.forEach((category) => {
        const source = monthData.expenseSources.find((s) => s.categoryId === category.categoryId)
        dataPoint[category.name] = source?.amount || 0
      })

      return dataPoint
    })
  }, [expenseSourcesByMonth, topCategories])

  // Calculate total spending across all months for top categories
  const totalTopCategories = topCategories.reduce((sum, cat) => sum + cat.total, 0)

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={cardEntrance}
      whileHover={getCardHoverEffect(isDark)}
      className="h-full min-h-0 flex flex-col"
    >
      <Card className="flex flex-col overflow-hidden h-full max-h-full border-border/50 bg-card/50 backdrop-blur-sm shadow-sm dark:shadow-none dark:border-border/50 transition-shadow hover:shadow-md dark:hover:shadow-none">
        {/* Accent border */}
        <div
          className={`h-1 absolute top-0 left-0 right-0 ${
            isDark
              ? "bg-gradient-to-r from-primary/60 via-primary/40 to-transparent"
              : "bg-gradient-to-r from-primary/80 via-primary/60 to-transparent"
          }`}
        />
        <CardContent className="p-6 flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="mb-4 flex-shrink-0">
            <CardTitle className="text-base font-medium mb-3 text-foreground">
              {t("components.dashboard.categoryComparison.title")}
            </CardTitle>
            <motion.div variants={statValue}>
              <StatValue className="text-3xl sm:text-4xl mb-2">
                {formatCurrency(totalTopCategories)}
              </StatValue>
            </motion.div>
            <StatLabel>
              {expenseSourcesByMonth.length > 1
                ? t("components.dashboard.categoryComparison.subtitleMultiple", {
                    count: topCategories.length,
                    months: expenseSourcesByMonth.length,
                  })
                : t("components.dashboard.categoryComparison.subtitle", {
                    count: topCategories.length,
                  })}
            </StatLabel>
          </div>

          {/* Chart */}
          <div className="flex-1 min-h-0">
            {chartData.length > 0 && topCategories.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: themeColors.cardForeground, fontSize: 12 }}
                  />
                  <YAxis
                    tick={{ fill: themeColors.cardForeground, fontSize: 12 }}
                    tickFormatter={(value) => formatCurrency(value)}
                  />
                  <Tooltip
                    cursor={false}
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null

                      const monthLabel = payload[0]?.payload?.monthLabel || label

                      return (
                        <div
                          className="rounded-md border bg-card px-3 py-2 text-sm shadow-md"
                          style={{
                            backgroundColor: themeColors.card,
                            borderColor: themeColors.border,
                            color: themeColors.cardForeground,
                          }}
                        >
                          <div className="font-medium mb-2">{monthLabel}</div>
                          {payload.map((entry: any, index: number) => {
                            const category = topCategories.find((cat) => cat.name === entry.dataKey)
                            const color = category?.color || entry.color

                            return (
                              <div key={index} className="flex items-center gap-2 mb-1 last:mb-0">
                                <div
                                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                  style={{
                                    backgroundColor: color,
                                    boxShadow: `0 0 4px ${color}40`,
                                  }}
                                />
                                <span className="font-medium">{entry.dataKey}:</span>
                                <span>
                                  <PrivacyBlur>{formatCurrency(entry.value as number)}</PrivacyBlur>
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      )
                    }}
                    wrapperStyle={{
                      outline: "none",
                    }}
                  />
                  <Legend
                    wrapperStyle={{
                      paddingTop: "10px",
                      fontSize: "12px",
                      color: themeColors.cardForeground,
                    }}
                    iconType="circle"
                  />
                  {topCategories.map((category) => (
                    <Bar
                      key={category.categoryId}
                      dataKey={category.name}
                      fill={category.color}
                      radius={[4, 4, 0, 0]}
                      activeBar={{
                        fill: category.color,
                        opacity: 0.7,
                        stroke: category.color,
                        strokeWidth: 1,
                      }}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-sm text-muted-foreground text-center">
                  {t("components.dashboard.categoryComparison.noData")}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
})

CategoryComparisonCard.displayName = "CategoryComparisonCard"
